/**
 * Rendering Minecraft item data for the web.
 *
 * Everything here handles player-controlled input: item names and lore are set by players, so
 * nothing in this file ever produces HTML. It returns arrays of {text, style} segments and React
 * renders them as text nodes, which escapes by construction — a `dangerouslySetInnerHTML` version of
 * the colour-code parser would be an XSS hole with an anvil as the input box.
 */

/**
 * Legacy colour codes, the `§0`-`§f` palette.
 *
 * These are the pre-1.16 colours the plugin still writes, and the hex values are the ones Minecraft
 * actually renders rather than the CSS names they are loosely called after — `§9 blue` is a mid
 * indigo, not `blue`, and using the CSS keyword makes kits look wrong to anyone who knows them.
 */
const COLOURS: Readonly<Record<string, string>> = {
  "0": "#000000", // black
  "1": "#0000aa", // dark blue
  "2": "#00aa00", // dark green
  "3": "#00aaaa", // dark aqua
  "4": "#aa0000", // dark red
  "5": "#aa00aa", // dark purple
  "6": "#ffaa00", // gold
  "7": "#aaaaaa", // grey
  "8": "#555555", // dark grey
  "9": "#5555ff", // blue
  a: "#55ff55", // green
  b: "#55ffff", // aqua
  c: "#ff5555", // red
  d: "#ff55ff", // light purple
  e: "#ffff55", // yellow
  f: "#ffffff", // white
};

export interface Segment {
  text: string;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  /** `§k`, the scrambling effect. Rendered as a static blur rather than animated — an item name that
   *  flickers forever in a staff tool is a distraction, not a feature. */
  obfuscated?: boolean;
}

/**
 * Splits a legacy-coded string into styled segments.
 *
 * Accepts both `§` and `&` as the marker: the plugin writes `§` in serialised item data, but staff
 * type `&` in configs and the two get mixed in practice. Unknown codes are dropped rather than
 * printed, so a stray marker never reaches the user as literal `§d`.
 */
export function parseLegacy(input: string): Segment[] {
  const segments: Segment[] = [];
  let current: Segment = { text: "" };

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i] as string;

    if ((char === "§" || char === "&") && i + 1 < input.length) {
      const code = (input[i + 1] as string).toLowerCase();
      const isColour = code in COLOURS;
      const isFormat = "klmnor".includes(code);
      if (!isColour && !isFormat) {
        current.text += char;
        continue;
      }

      if (current.text) segments.push(current);

      if (isColour) {
        // A colour resets formatting in Minecraft — this is not an oversight, it is how the game
        // behaves, and matching it is what makes a pasted kit look right.
        current = { text: "", color: COLOURS[code] as string };
      } else if (code === "r") {
        current = { text: "" };
      } else {
        current = { ...current, text: "" };
        if (code === "l") current.bold = true;
        if (code === "m") current.strike = true;
        if (code === "n") current.underline = true;
        if (code === "o") current.italic = true;
        if (code === "k") current.obfuscated = true;
      }
      i += 1;
      continue;
    }

    current.text += char;
  }

  if (current.text) segments.push(current);
  return segments;
}

/** The same string with all formatting removed — for `title` attributes and copy buttons, where
 *  markup is not wanted but raw `§d` would be worse. */
export function stripLegacy(input: string): string {
  return parseLegacy(input)
    .map((segment) => segment.text)
    .join("");
}

/**
 * `NETHERITE_PICKAXE` to `Netherite Pickaxe`.
 *
 * A lookup table would be more faithful but needs maintaining against every Minecraft release; the
 * mechanical transform is right for essentially every material and wrong only for a handful of
 * oddities nobody dupes.
 */
export function prettyMaterial(material: string): string {
  return material
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const ROMAN: ReadonlyArray<[number, string]> = [
  [10, "X"],
  [9, "IX"],
  [5, "V"],
  [4, "IV"],
  [1, "I"],
];

/**
 * Enchantment levels as Minecraft shows them: `Efficiency V`, and level 1 as just the name.
 *
 * Levels above ten fall back to the digit. Roman numerals past X are not something the game renders
 * either, and `Sharpness XXXII` on a hacked item would be less legible than `Sharpness 32` — which
 * is exactly the case where staff most need to read the number.
 */
export function romanLevel(level: number): string {
  if (!Number.isFinite(level) || level < 1) return "";
  if (level === 1) return "";
  if (level > 10) return String(level);

  let remaining = Math.floor(level);
  let out = "";
  for (const [value, numeral] of ROMAN) {
    while (remaining >= value) {
      out += numeral;
      remaining -= value;
    }
  }
  return out;
}

export interface Enchantment {
  key?: string;
  name?: string;
  level?: number;
}

/** `Efficiency V`. Falls back to the namespaced key when the display name is missing, so an
 *  unrecognised enchantment still shows something identifiable. */
export function enchantmentLabel(enchantment: Enchantment): string {
  const name =
    enchantment.name ??
    (enchantment.key ? prettyMaterial(enchantment.key.replace(/^minecraft:/, "")) : "Unknown");
  const level = romanLevel(Number(enchantment.level ?? 1));
  return level ? `${name} ${level}` : name;
}

/** Remaining life of a custom tool, computed from `expires_at` at render time.
 *
 *  Deliberately not `millis_left`: that was captured when the row was written and is stale by
 *  however long ago the incident was, which for anything older than a day reads as nonsense. */
export function toolRemaining(expiresAt: number | undefined, expired: boolean | undefined): string {
  if (expired) return "Expired";
  if (!expiresAt || !Number.isFinite(expiresAt)) return "Unknown";

  const left = expiresAt - Date.now();
  if (left <= 0) return "Expired";

  const days = Math.floor(left / 86_400_000);
  const hours = Math.floor((left % 86_400_000) / 3_600_000);
  const minutes = Math.floor((left % 3_600_000) / 60_000);
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
}
